import type { ReactNode } from "react";
import React, { createContext, useContext, useState, useCallback } from "react";

import axios, { type AxiosResponse } from "axios";

import type {
  ProviderNested,
  ProviderDirectoryProvidersList200Response,
} from "~/src/types";

export interface ProviderDataProps {
  results: ProviderNested[];
  loading?: boolean;
  error?: string | null;
  count?: number;
}

interface ProviderProfileContextType {
  providerData: ProviderDataProps;
  fetchProviders: (searchParams?: string) => void;
  providerResults: ProviderNested[];
  setProviderData: (data: ProviderDataProps) => void;
  setProviderResults: (results: ProviderNested[]) => void;
}

const ProviderProfileContext = createContext<
  ProviderProfileContextType | undefined
>(undefined);

export const ProviderProfileProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [providerData, setProviderData] = useState<ProviderDataProps>({
    results: [],
    loading: true,
    error: null,
  });

  const fetchProviders = useCallback((searchParams: string = "") => {
    setProviderData((prev) => {
      return {
        ...prev,
        loading: true,
      };
    });

    axios
      .get(
        `provider_directory/providers${searchParams.startsWith("?") ? "" : "?"}${searchParams}`,
      )
      .then((res: AxiosResponse<ProviderDirectoryProvidersList200Response>) => {
        setProviderData({
          results: res.data.result?.results || [],
          count: res.data.result?.count || 0,
          loading: false,
          error: null,
        });
      })
      .catch((err) => {
        console.error(err);
        setProviderData({
          results: [],
          loading: false,
          error: "Something went wrong loading providers",
        });
      });
  }, []);

  const setProviderDataFunc = useCallback((data: ProviderDataProps) => {
    setProviderData((prev) => {
      return {
        ...prev,
        ...data,
      };
    });
  }, []);

  const setProviderResults = useCallback((results: ProviderNested[]) => {
    setProviderData((prev) => {
      return {
        ...prev,
        results,
        loading: false,
      };
    });
  }, []);

  const contextValue: ProviderProfileContextType = {
    providerData,
    fetchProviders,
    providerResults: providerData.results,
    setProviderData: setProviderDataFunc,
    setProviderResults,
  };

  return (
    <ProviderProfileContext.Provider value={contextValue}>
      {children}
    </ProviderProfileContext.Provider>
  );
};

export const useProviderProfiles = () => {
  const context = useContext(ProviderProfileContext);

  if (context === undefined) {
    throw new Error(
      "useProviderProfiles must be used within a ProviderProfileContext",
    );
  }

  return context;
};
